import { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import MenuItem from '@mui/material/MenuItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Alert from '@mui/material/Alert';

import { TCreateProviderRequest, TFieldSchema, TFieldScope, TFieldType } from '../schemas/payment-provider-schema';
import { STARTER_SCHEMAS } from '../constants';

type ProviderCreateDialogProps = {
  open: boolean;
  onClose: () => void;
  onSave: (payload: TCreateProviderRequest) => void;
  isSaving?: boolean;
};

const blankField = (): TFieldSchema => ({
  id: '',
  type: 'text',
  scope: 'global',
  required: false,
  displayOrder: 1,
  label: '',
});

const ProviderCreateDialog = ({ open, onClose, onSave, isSaving }: ProviderCreateDialogProps) => {
  const [providerId, setProviderId] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [isEnabled, setIsEnabled] = useState(true);
  const [fields, setFields] = useState<TFieldSchema[]>([blankField()]);
  const [globalValues, setGlobalValues] = useState<Record<string, string>>({});
  const [template, setTemplate] = useState('');

  useEffect(() => {
    if (open) {
      setProviderId('');
      setDisplayName('');
      setIsEnabled(true);
      setFields([blankField()]);
      setGlobalValues({});
      setTemplate('');
    }
  }, [open]);

  const applyTemplate = (id: string) => {
    setTemplate(id);
    const preset = STARTER_SCHEMAS.find((s) => s.providerId === id);
    if (!preset) return;
    setProviderId(preset.providerId);
    setDisplayName(preset.displayName);
    setFields(preset.fields.map((f) => ({ ...f })));
    setGlobalValues({});
  };

  const updateField = (idx: number, patch: Partial<TFieldSchema>) => {
    setFields((prev) =>
      prev.map((f, i) => (i === idx ? { ...f, ...patch } : f)),
    );
  };

  const removeField = (idx: number) => {
    setFields((prev) => prev.filter((_, i) => i !== idx));
  };

  const addField = () => {
    setFields((prev) => [...prev, { ...blankField(), displayOrder: prev.length + 1 }]);
  };

  const idValid = /^[a-z][a-z0-9_-]{1,39}$/.test(providerId);
  const fieldsValid =
    fields.length > 0 &&
    fields.every((f) => /^[a-z][a-z0-9_-]{0,63}$/.test(f.id)) &&
    new Set(fields.map((f) => f.id)).size === fields.length;

  // Required global fields must be filled here — nothing else enforces them, and
  // a provider missing its Foodo-level credentials is unusable on every kiosk.
  const missingRequiredGlobals = fields
    .filter((f) => (f.scope ?? 'global') === 'global' && f.required)
    .filter((f) => !(globalValues[f.id] || '').trim())
    .map((f) => f.id);

  const canSave =
    idValid &&
    displayName.trim().length > 0 &&
    fieldsValid &&
    missingRequiredGlobals.length === 0;

  const handleSave = () => {
    const globalFields: Record<string, string> = {};
    fields
      .filter((f) => (f.scope ?? 'global') === 'global')
      .forEach((f) => {
        const v = globalValues[f.id];
        if (v !== undefined && v !== '') globalFields[f.id] = v;
      });

    onSave({
      providerId,
      displayName: displayName.trim(),
      isEnabled,
      fields: fields.map((f) => ({
        id: f.id,
        type: f.type,
        scope: f.scope || 'global',
        required: !!f.required,
        displayOrder: f.displayOrder ?? 0,
        label: f.label?.trim() || undefined,
      })),
      globalFields: Object.keys(globalFields).length > 0 ? globalFields : undefined,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={styles.title}>
        Register Payment Provider
        <Typography sx={styles.subtitle}>
          One-shot create: provider row + field schema + initial Foodo-global values.
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={styles.body}>
          <TextField
            select
            label="Start from a template"
            size="small"
            fullWidth
            value={template}
            onChange={(e) => applyTemplate(e.target.value)}
            disabled={isSaving}
            helperText="Acquirer-verified field ids. Everything stays editable after applying."
          >
            <MenuItem value="">
              <em>None — define fields manually</em>
            </MenuItem>
            {STARTER_SCHEMAS.map((s) => (
              <MenuItem key={s.providerId} value={s.providerId}>
                {s.displayName}
              </MenuItem>
            ))}
          </TextField>

          <Box sx={styles.row}>
            <TextField
              label="Provider ID *"
              size="small"
              fullWidth
              value={providerId}
              onChange={(e) => setProviderId(e.target.value)}
              disabled={isSaving}
              helperText="lowercase slug. e.g. dojo, sumup_solo. Immutable."
            />
            <TextField
              label="Display Name *"
              size="small"
              fullWidth
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              disabled={isSaving}
            />
          </Box>

          <FormControlLabel
            control={
              <Switch
                checked={isEnabled}
                onChange={(e) => setIsEnabled(e.target.checked)}
                disabled={isSaving}
              />
            }
            label={isEnabled ? 'Enabled — visible to kiosks once globals are set' : 'Disabled'}
          />

          <Alert severity="info" sx={styles.immutableNote}>
            Field <strong>id</strong>, <strong>type</strong> and <strong>scope</strong> are
            immutable once created — only label, required and display order can be changed later.
            Ids are case-sensitive and must match the kiosk app exactly.
          </Alert>

          <Box sx={styles.sectionHeader}>
            <Typography sx={styles.sectionTitle}>Fields Schema</Typography>
            <Button
              size="small"
              startIcon={<AddIcon fontSize="small" />}
              onClick={addField}
              sx={styles.addBtn}
              disabled={isSaving}
            >
              Add Field
            </Button>
          </Box>

          {fields.length === 0 ? (
            <Alert severity="info">Add at least one field.</Alert>
          ) : (
            <Box sx={styles.fieldList}>
              {fields.map((f, idx) => (
                <Box key={idx} sx={styles.fieldCard}>
                  <Box sx={styles.fieldRow}>
                    <Tooltip title="id, type and scope are immutable once the provider is created. Field ids are case-sensitive and must match the kiosk app exactly.">
                      <TextField
                        label="ID *"
                        size="small"
                        value={f.id}
                        onChange={(e) => updateField(idx, { id: e.target.value })}
                        disabled={isSaving}
                        sx={{ flex: 2 }}
                      />
                    </Tooltip>
                    <TextField
                      select
                      label="Type"
                      size="small"
                      value={f.type}
                      onChange={(e) => updateField(idx, { type: e.target.value as TFieldType })}
                      disabled={isSaving}
                      sx={{ flex: 1 }}
                    >
                      <MenuItem value="text">text</MenuItem>
                      <MenuItem value="secret">secret</MenuItem>
                    </TextField>
                    <TextField
                      select
                      label="Scope"
                      size="small"
                      value={f.scope || 'global'}
                      onChange={(e) => updateField(idx, { scope: e.target.value as TFieldScope })}
                      disabled={isSaving}
                      sx={{ flex: 1 }}
                    >
                      <MenuItem value="global">global</MenuItem>
                      <MenuItem value="client">client</MenuItem>
                      <MenuItem value="device">device</MenuItem>
                    </TextField>
                    <TextField
                      label="Order"
                      size="small"
                      type="number"
                      value={f.displayOrder ?? 0}
                      onChange={(e) => updateField(idx, { displayOrder: Number(e.target.value) || 0 })}
                      disabled={isSaving}
                      sx={{ width: 80 }}
                    />
                    <Tooltip title="If on, this field must be filled in before the provider can be saved.">
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={!!f.required}
                            onChange={(e) => updateField(idx, { required: e.target.checked })}
                            disabled={isSaving}
                          />
                        }
                        label="Required"
                        sx={styles.requiredToggle}
                      />
                    </Tooltip>
                    <Tooltip title="Remove field">
                      <IconButton
                        size="small"
                        onClick={() => removeField(idx)}
                        disabled={isSaving}
                        sx={{ color: '#dc2626' }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <TextField
                    label="Label (optional)"
                    size="small"
                    fullWidth
                    value={f.label || ''}
                    onChange={(e) => updateField(idx, { label: e.target.value })}
                    disabled={isSaving}
                  />
                  {(f.scope === 'global' || !f.scope) && f.id && (
                    <TextField
                      label={`Initial value for ${f.id} (optional)`}
                      size="small"
                      fullWidth
                      type={f.type === 'secret' ? 'password' : 'text'}
                      value={globalValues[f.id] || ''}
                      onChange={(e) =>
                        setGlobalValues((prev) => ({ ...prev, [f.id]: e.target.value }))
                      }
                      disabled={isSaving}
                    />
                  )}
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={styles.actions}>
        {missingRequiredGlobals.length > 0 && (
          <Typography sx={styles.blockedText}>
            Fill required global field{missingRequiredGlobals.length > 1 ? 's' : ''}:{' '}
            {missingRequiredGlobals.join(', ')}
          </Typography>
        )}
        <Button sx={styles.cancelBtn} onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button sx={styles.saveBtn} onClick={handleSave} disabled={!canSave || isSaving}>
          {isSaving ? 'Creating…' : 'Create Provider'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProviderCreateDialog;

const styles = {
  title: { fontFamily: 'GilroyBold', fontSize: 18 },
  subtitle: { fontFamily: 'GilroyRegular', fontSize: 13, color: '#666', mt: 0.5 },
  body: { display: 'flex', flexDirection: 'column' as const, gap: 2, pt: 1 },
  row: { display: 'flex', gap: 2 },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    mt: 1,
  },
  sectionTitle: {
    fontFamily: 'GilroyBold',
    fontSize: 14,
    color: '#0f172a',
  },
  addBtn: {
    textTransform: 'capitalize' as const,
    color: '#0f172a',
    fontFamily: 'GilroySemiBold',
    fontSize: 12,
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    backgroundColor: '#ffffff',
    px: 1.5,
    '&:hover': { backgroundColor: '#f1f5f9' },
  },
  fieldList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 1.5,
  },
  fieldCard: {
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    padding: 1.5,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 1.5,
    backgroundColor: '#f8fafc',
  },
  fieldRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
  },
  requiredToggle: {
    minWidth: 100,
    ml: 0,
    '& .MuiFormControlLabel-label': {
      fontFamily: 'GilroySemiBold',
      fontSize: 13,
      color: '#0f172a',
    },
  },
  immutableNote: {
    fontFamily: 'GilroyRegular',
    fontSize: 13,
    py: 0,
  },
  blockedText: {
    fontFamily: 'GilroyRegular',
    fontSize: 12,
    color: '#b91c1c',
    mr: 'auto',
  },
  actions: { px: 3, py: 2 },
  cancelBtn: {
    textTransform: 'capitalize' as const,
    color: '#000',
    fontFamily: 'GilroySemiBold',
    border: '1px solid #000',
    borderRadius: '8px',
    px: 2.5,
  },
  saveBtn: {
    textTransform: 'capitalize' as const,
    backgroundColor: '#000',
    color: '#FFF',
    fontFamily: 'GilroySemiBold',
    borderRadius: '8px',
    px: 2.5,
    '&:hover': { backgroundColor: '#333' },
    '&:disabled': { opacity: 0.5, color: '#FFF' },
  },
};
